/**
 * Notification helpers — human labels + navigation targets for notifications.
 *
 * (Reconstructed from usage after the original source was lost; matches the
 * call sites in Header.tsx / HomePage.tsx / useNotificationPolling.ts.)
 */

const KIND_LABELS: Record<string, string> = {
  morning_brief: 'Morning brief',
  weekly_review: 'Weekly review',
  task_due: 'Task due',
  task_overdue: 'Task overdue',
  suggestion: 'Suggestion',
  suggestions: 'Suggestions',
  connection: 'Connection',
  connections: 'Connections',
  priority: 'Priority alert',
  forgotten: 'Worth revisiting',
};

/** Map a notification `kind` to a short, human-readable label. */
export function getNotificationLabel(kind?: string | null): string {
  if (!kind) return 'Notification';
  if (KIND_LABELS[kind]) return KIND_LABELS[kind];
  return kind
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface NotificationLike {
  kind?: string | null;
  target_type?: string | null;
  target_id?: string | null;
  payload?: Record<string, unknown> | null;
}

/** List route (no id) for a notification kind — the fallback when no id is known. */
const KIND_LIST_ROUTE: Record<string, string> = {
  task: '/tasks',
  priority: '/tasks',
  forgotten: '/knowledge',
  knowledge: '/knowledge',
  connection: '/connections',
  connections: '/connections',
  suggestion: '/suggestions',
  suggestions: '/suggestions',
};

function firstString(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (typeof v === 'string' && v.trim()) return v.trim();
    if (typeof v === 'number') return String(v);
  }
  return undefined;
}

/**
 * Build the in-app deep-link (hash-relative, e.g. "/knowledge?item_id=abc") a
 * notification should open. Prefers the SPECIFIC entity from the payload
 * (item_id → that knowledge item, task_id → that task, …); falls back to the
 * relevant list page by kind; returns null when nothing is actionable.
 *
 * Callers add the leading "#": in-app sets `window.location.hash`, the service
 * worker uses `/#<url>` as the notification's click target.
 */
export function getNotificationUrl(notification: NotificationLike): string | null {
  if (!notification) return null;
  const p = notification.payload || {};

  const itemId = firstString(p.item_id, p.knowledge_id, p.knowledge_item_id);
  if (itemId) return `/knowledge?item_id=${encodeURIComponent(itemId)}`;

  const taskId = firstString(p.task_id);
  if (taskId) return `/tasks?task_id=${encodeURIComponent(taskId)}`;

  const projectId = firstString(p.project_id);
  if (projectId) return `/projects?project_id=${encodeURIComponent(projectId)}`;

  const suggestionId = firstString(p.suggestion_id);
  if (suggestionId) return `/suggestions?suggestion_id=${encodeURIComponent(suggestionId)}`;

  // Explicit target_type/target_id (future-proofing for structured notifications).
  const tType = notification.target_type || undefined;
  const tId = notification.target_id || undefined;
  if (tType && tId) {
    const base = KIND_LIST_ROUTE[tType];
    if (base) {
      const param = base === '/knowledge' ? 'item_id' : `${tType}_id`;
      return `${base}?${param}=${encodeURIComponent(tId)}`;
    }
  }

  // No id — fall back to the list page for this kind (better than always /knowledge).
  const kind = (notification.kind || '').toLowerCase();
  for (const key of Object.keys(KIND_LIST_ROUTE)) {
    if (kind.includes(key)) return KIND_LIST_ROUTE[key];
  }
  return null;
}

/**
 * Derive the in-app hash route a notification should navigate to, or null when
 * the notification isn't actionable. Used as `window.location.hash = #<target>`.
 */
export function getNotificationTarget(notification: NotificationLike): string | null {
  if (!notification) return null;

  const targetType = notification.target_type || undefined;
  const targetId = notification.target_id || undefined;
  if (targetType && targetId) {
    return `${targetType}/${targetId}`;
  }
  if (targetType) {
    return targetType;
  }

  const kind = notification.kind || '';
  if (kind.includes('task')) return 'tasks';
  if (kind.includes('project')) return 'projects';
  if (kind.includes('suggestion')) return 'suggestions';
  if (kind.includes('connection')) return 'connections';
  if (kind.includes('knowledge')) return 'knowledge';

  return null;
}
